import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const yearLabel = (y: number) => `${y}年（${toWareki(y)}）`;

/** What in a permits-and-paperwork article changes: fees, requirements, deadlines, dates, reforms. */
const GYOSEI_SENSITIVITY: SensitivityRules = {
  amount: { label: "金額・手数料", pattern: /\d[\d,，.]*\s?(?:万円|円|%|％)|手数料|収入印紙|報酬/ },
  relief: { label: "許可の要件・制度", pattern: /要件|許可|認可|届出|対象(?:と|に)なる|制度|特例|補助金/ },
  deadline: { label: "期限・有効期間", pattern: /期限|有効期間|更新|(?:\d+|[一二三四五六七八九十]+)(?:日|か月|ヶ月|カ月|年)以内|月\d+日まで/ },
  year: { label: "時点の書き方", pattern: /令和\d+年|\d{4}年(?:時点|現在|\d+月)|施行日/ },
  reform: { label: "改正・施行の記述", pattern: /改正|施行|義務化|予定|見込み|新設|廃止|創設|電子申請/ },
};

/**
 * 行政書士. The expression rules follow the 行政書士法 and the federation's
 * advertising guidance: no promised permits, no comparison with other
 * 行政書士, and no legal work reserved to other professions.
 */
export const ADMINISTRATIVE_SCRIVENER: ProfessionPack = {
  id: "administrative_scrivener",
  label: "行政書士",
  supervisorTitle: "行政書士",
  registrationLabel: "行政書士登録番号",
  reviewer: "行政書士の先生",
  writingRules: [
    "依頼主の行政書士事務所の立場で書く。文体は「です・ます」。一人称と表記は事務所の設定に従う。",
    "断定してよいのは、法令、所管の省庁や自治体の公表資料などの公的な一次資料に根拠がある場合だけ。根拠とした資料を出典として示す。",
    "許可の要件・手数料・標準処理期間・有効期間などは、一次資料で確認できたものだけを書く。自治体で異なるものは「自治体によって異なります」と書く。",
    "改正は施行日を明記し、施行前のものは「予定」と書く。",
    "許可の取得を保証する表現（「必ず許可が下りる」「100% 取得」など）、根拠のない最上級、他の行政書士事務所との比較は書かない。",
    "行政書士が扱えない業務（紛争の代理、登記、税務申告、裁判所に提出する書類など）を扱えるように書かない。超える内容は他の士業との連携として書く。",
    "個別の相談に当たる断定的な助言はしない。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    { id: "gyosei-permit-guarantee", pattern: /(必ず|確実に|絶対に?)(許可|認可|取得|通り|採択|下り)|許可(を)?保証|取得率\s?100/g, severity: "high", category: "許可の保証", message: "許可や認可の取得を保証する表現は使えません", suggestion: "「要件を満たせば許可される見込みがあります」とし、要件と審査を説明する" },
    { id: "gyosei-comparison", pattern: /他の(行政書士|事務所)(より|と比べ|とは違|には(でき|ない))|どこよりも/g, severity: "high", category: "他の行政書士との比較", message: "他の行政書士事務所との比較は、広告として認められていません", suggestion: "比較をやめ、当事務所の取り扱い方を事実として説明する" },
    { id: "gyosei-scope", pattern: /(登記|税務申告|確定申告|訴訟|調停|裁判)(を|も|の|まで)?(代行|代理)|示談交渉/g, severity: "high", category: "扱えない業務", message: "行政書士が扱えない業務を扱えるように読める表現です", suggestion: "「司法書士・税理士・弁護士と連携して対応します」など、扱える範囲に書き直す" },
    { id: "gyosei-individual", pattern: /あなたは.{0,20}(すべき|しなければなりません|取得できます|対象です)/g, severity: "medium", category: "個別の助言", message: "個別の事情によらず断定する助言になっています", suggestion: "「〜のような場合は、〜の対象になる可能性があります。詳しくはご相談ください」にする" },
    { id: "gyosei-future", pattern: /改正(され|になり)ます|(施行|適用)されます/g, severity: "low", category: "改正の書き方", message: "施行前の内容なら、施行日と「予定」を明記する必要があります", suggestion: "「〜年〜月〜日から施行される予定です（〇年〇月時点）」と書く" },
  ],
  trustedDomains: ["laws.e-gov.go.jp", "elaws.e-gov.go.jp", "moj.go.jp", "isa.go.jp", "mlit.go.jp", "mhlw.go.jp", "meti.go.jp", "chusho.meti.go.jp", "npa.go.jp", "maff.go.jp", "env.go.jp", "jpo.go.jp", "nta.go.jp", "gyosei.or.jp", "jgrants-portal.go.jp", "mirasapo-plus.go.jp"],
  authorityDomains: ["bengo4.com", "freee.co.jp", "biz.moneyforward.com", "nikkei.com", "ja.wikipedia.org", "gyoseishoshi-navi.com"],
  specialtyOptions: ["建設業許可", "産業廃棄物・古物商などの許可", "飲食店・風俗営業の許可", "運送業・自動車の手続き", "外国人の在留資格（ビザ）", "会社設立・定款", "相続・遺言（遺産分割協議書）", "補助金・事業計画", "農地転用・開発許可", "契約書・内容証明", "医療法人・介護事業の指定", "ドローン・各種届出"],
  seasons: [
    { id: "nendo-matsu", name: "年度末の許可の更新と新年度の準備", start: { month: 1, day: 15 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["建設業許可 更新", "決算変更届", "経営事項審査", "許可 更新", "入札参加資格"], note: "決算変更届や更新の手続きが重なります。新年度の公共工事に向けた準備も始まります。" },
    { id: "zairyu-haru", name: "新年度の外国人の雇用と在留", start: { month: 2, day: 1 }, end: { month: 4, day: 30 }, leadWeeks: 8, terms: ["在留資格", "ビザ 変更", "特定技能", "技術・人文知識・国際業務", "就労ビザ"], note: "4月の入社と卒業に合わせて、在留資格の変更や取得の手続きが増えます。" },
    { id: "hojokin-haru", name: "補助金の公募（春）", start: { month: 3, day: 1 }, end: { month: 6, day: 30 }, leadWeeks: 8, terms: ["補助金", "小規模事業者持続化補助金", "ものづくり補助金", "事業計画書"], note: "主な補助金の公募が春に集まります。公募の前に公開しておくと、検索が増える時期に間に合います。" },
    { id: "natsu-kaigyo", name: "夏の開業・創業", start: { month: 6, day: 1 }, end: { month: 8, day: 31 }, leadWeeks: 6, terms: ["会社設立", "開業", "飲食店 許可", "古物商", "創業融資"], note: "上半期の区切りで開業や新しい事業を始める人が調べる時期です。" },
    { id: "obon", name: "お盆の帰省（相続・遺言の話し合い）", start: { month: 8, day: 1 }, end: { month: 8, day: 20 }, leadWeeks: 6, terms: ["遺言書", "遺産分割協議書", "相続 手続き", "自動車 相続"], note: "家族が集まり、相続や遺言の話題が出やすい時期です。" },
    { id: "nenmatsu", name: "年末の整理と来年の計画", start: { month: 11, day: 1 }, end: { month: 12, day: 31 }, leadWeeks: 8, terms: ["許可 有効期限", "更新 忘れ", "変更届", "来年 制度 改正"], note: "年内に済ませたい届出と、来年の改正に備える検索が増えます。" },
  ],
  officeNoun: "行政書士事務所",
  officeExample: "山田行政書士事務所",
  affiliationExample: "大阪府行政書士会",
  sourcesPhrase: "所管の省庁や自治体などの公的な資料",
  sourceExamples: "e-Gov 法令検索・所管の省庁（国土交通省・出入国在留管理庁など）",
  keyFigures: "手数料・要件・期限",
  publishers: { "e-gov.go.jp": "e-Gov 法令検索", "moj.go.jp": "法務省", "isa.go.jp": "出入国在留管理庁", "mlit.go.jp": "国土交通省", "mhlw.go.jp": "厚生労働省", "chusho.meti.go.jp": "中小企業庁", "meti.go.jp": "経済産業省", "npa.go.jp": "警察庁", "maff.go.jp": "農林水産省", "env.go.jp": "環境省", "jpo.go.jp": "特許庁", "nta.go.jp": "国税庁", "gyosei.or.jp": "日本行政書士会連合会", "jgrants-portal.go.jp": "jGrants（補助金の電子申請）", "mirasapo-plus.go.jp": "ミラサポ plus" },
  period: {
    label: yearLabel,
    short: (y) => `${y}年`,
    rules: (y) => `${yearLabel(y)}時点の法令・制度`,
    brief: (y) => `基準とする時点：${yearLabel(y)}時点の法令・制度（現行のものを書く。改正は施行日を明記し、施行前のものは「予定」と書く。自治体で異なるものはその旨を書く）`,
    mustState: false,
    current: tokyoYear,
  },
  review: { name: "法改正の見直し", when: "法改正の施行や、手数料・様式の変更のあと", nowLabel: "現在", currentArticle: "最新の法令・制度に合った記事", updateLabel: "法改正の反映", sensitivity: GYOSEI_SENSITIVITY },
  features: { articles: true },
};
